import { useState, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRoute,
  createRoute,
  createRouter,
  RouterProvider,
  type RouterHistory,
} from '@tanstack/react-router';
import { AuthProvider, useAuth } from './lib/auth.js';
import { installTheme } from './theme.js';
import { queryClient } from './features/queryClient.js';
import { AgentDetail } from './features/agents/AgentDetail.js';
import { AgentRunDetail } from './features/agents/AgentRunDetail.js';
import { Agents, AGENTS_READ_KEY, AGENTS_IS } from './features/agents/Agents.js';
import { AgentDeploy, AGENT_DEPLOY_IS } from './features/agents/AgentDeploy.js';
import {
  TalkToPhoebe,
  TALK_TO_PHOEBE_IS,
  TALK_TO_PHOEBE_KEY,
} from './features/virtual-workforce/TalkToPhoebe.js';
import { ShellLayout } from './features/shell/ShellLayout.js';
import { RightNow } from './features/right-now/RightNow.js';
import { TodayReconciled } from './features/right-now/TodayReconciled.js';
import { Attention } from './features/attention/Attention.js';
import { SignIn } from './features/signin/SignIn.js';
import { SelectOrg } from './features/signin/SelectOrg.js';
import { AcceptInvite } from './features/signin/AcceptInvite.js';
import { EnrollMfa } from './features/signin/EnrollMfa.js';
import { signedInAs } from './lib/authnIdentity.js';
import { isSelectOrgPath } from './lib/selectOrgPath.js';
import { inviteTokenFromLocation, isInvitePath } from './lib/invitePath.js';
import { TeamBoard } from './features/people/TeamBoard.js';
import { SessionDetail } from './features/sessions/SessionDetail.js';
import { TimeClockLayout, TimeOverview, WhosWorking } from './features/time/TimeClock.js';
import { Approvals, TimesheetDetail, TimesheetList } from './features/time/Timesheets.js';
import { TimeExceptions } from './features/time/TimeExceptions.js';
import { WorkerProfile } from './features/people/WorkerProfile.js';
import { Scheduling } from './features/schedule/Scheduling.js';
import { CancellationQueue } from './features/schedule/CancellationQueue.js';
import { Visits } from './features/visits/Visits.js';
import { Workforce } from './features/workforce/Workforce.js';
import { Dashboard } from './features/dashboard/Dashboard.js';
import { ConfigurableDashboard } from './features/dashboard/configurable/ConfigurableDashboard.js';
import { Messages } from './features/messages/Messages.js';
import { Mileage } from './features/mileage/Mileage.js';
import { NotificationCenter } from './features/notifications/NotificationCenter.js';
import { ChannelThread } from './features/messages/ChannelThread.js';
import { RequirePermission } from './features/RequirePermission.js';
import { ADMIN_VIEWS_READ, keysThatOpen, usePermissions } from './features/permissions.js';
import { AuditLog } from './features/compliance/AuditLog.js';
import { ComplianceCenter } from './features/compliance/ComplianceCenter.js';
import { InsightDetail } from './features/insights/InsightDetail.js';
import { Insights, INSIGHTS_IS } from './features/insights/Insights.js';
import { EscalationQueue, ESCALATIONS_IS } from './features/insights/EscalationQueue.js';
import { RecommendationRecordDetail } from './features/intelligence/records/RecommendationRecordDetail.js';
import {
  RecommendationRecords,
  RECOMMENDATION_RECORDS_IS,
  RECOMMENDATION_READ_KEY,
} from './features/intelligence/records/RecommendationRecords.js';
import { Automations } from './features/intelligence/Automations.js';
import { Library, LIBRARY_IS, LIBRARY_READ_KEY } from './features/library/Library.js';
import { RuleDetail } from './features/library/RuleDetail.js';
import { SourceDetail } from './features/library/SourceDetail.js';
import { EligibilityCenter } from './features/compliance/EligibilityCenter.js';
import { EligibilityWorkerDetail } from './features/compliance/EligibilityWorkerDetail.js';
import { FormDetail } from './features/compliance/FormDetail.js';
import { FormResponseReader } from './features/compliance/FormResponseReader.js';
import { FormsCenter } from './features/compliance/FormsCenter.js';
import { IncidentDetail } from './features/compliance/IncidentDetail.js';
import { IncidentsCenter } from './features/compliance/IncidentsCenter.js';
import { JurisdictionsSettings } from './features/settings/JurisdictionsSettings.js';
import { MeProfile } from './features/settings/MeProfile.js';
import { HelpSupport } from './features/settings/HelpSupport.js';
import { SettingsIndex } from './features/settings/SettingsIndex.js';
import { SettingsLayout } from './features/settings/SettingsLayout.js';
import { OnboardingChecklist } from './features/onboarding/OnboardingChecklist.js';
import { OrgSettings } from './features/settings/OrgSettings.js';
import { PeopleSettings } from './features/settings/PeopleSettings.js';
import { PersonDetail } from './features/settings/PersonDetail.js';
// The /settings/location family points at the Location & geofencing hub
// (docs/LOCATION-GEOFENCING-DESIGN.md; L115 shell + policy pages, L116 Work
// Sites lens + Geofences CRUD — which retired the old single-page
// LocationSettings.tsx entirely).
import { LocationGeneralPage } from './features/location-settings/GeneralPage.js';
import { ProgramPolicyPage } from './features/location-settings/ProgramPolicyPage.js';
import { LocationTrackingPage } from './features/location-settings/TrackingPage.js';
import { LocationClockRulesPage } from './features/location-settings/ClockRulesPage.js';
import { LocationExceptionsPage } from './features/location-settings/ExceptionsPage.js';
import { LocationPrivacyPage } from './features/location-settings/PrivacyPage.js';
import { LocationSitesPage } from './features/location-settings/SitesPage.js';
import { LocationGeofencesPage } from './features/location-settings/GeofencesPage.js';
import { LocationActivityPage } from './features/location-settings/ActivityPage.js';
import { RolesPermissions } from './features/roles-permissions/RolesPermissions.js';
import { ProgramsSettings } from './features/settings/ProgramsSettings.js';
import { ProgramDetail } from './features/settings/ProgramDetail.js';
import { SitesSettings } from './features/settings/SitesSettings.js';
import { SiteDetail } from './features/settings/SiteDetail.js';
import { RulesSettings } from './features/settings/RulesSettings.js';
import { ServicesSettings } from './features/settings/ServicesSettings.js';
import { TimeoffSettings } from './features/settings/TimeoffSettings.js';
import { NotificationsSettings } from './features/settings/NotificationsSettings.js';
import { NotificationPreferences } from './features/settings/NotificationPreferences.js';
import { MemoryAboutMe } from './features/settings/MemoryAboutMe.js';
import { RecordsSettings } from './features/settings/RecordsSettings.js';
import { MileageSettings } from './features/settings/MileageSettings.js';
import { AttentionBudgetSettings } from './features/settings/AttentionBudgetSettings.js';
import { IntelligenceSettings } from './features/settings/IntelligenceSettings.js';
import { EscalationTargetsSettings } from './features/settings/EscalationTargetsSettings.js';
import { WorkerPreview } from './features/worker-preview/WorkerPreview.js';
import { DocumentationQueue } from './features/documentation/DocumentationQueue.js';
import { ExportsScreen } from './features/reporting/ExportsScreen.js';
import { SupervisionCenter } from './features/compliance/SupervisionCenter.js';
import { SupervisionWorkerDetail } from './features/compliance/SupervisionWorkerDetail.js';
import { CredentialsCenter } from './features/compliance/CredentialsCenter.js';
import { CredentialsWorkerDetail } from './features/compliance/CredentialsWorkerDetail.js';
import { DocumentationDetail } from './features/documentation/DocumentationDetail.js';
import {
  COMPLETENESS_IS,
  COMPLETENESS_READ_KEY,
  DocumentationCompleteness,
} from './features/documentation/DocumentationCompleteness.js';
import { TasksQueue, TASK_READ_KEY } from './features/tasks/TasksQueue.js';
import { TaskDetail } from './features/tasks/TaskDetail.js';
import { OrgChart } from './features/orgchart/OrgChart.js';
import { Intelligence, INTELLIGENCE_IS } from './features/intelligence/Intelligence.js';

/**
 * Admin Web entry (doc 10.1). The working OIDC auth/session wiring in
 * `lib/auth.tsx` + `lib/api.ts` is preserved exactly: `useAuth()` bootstraps via
 * `GET /auth/me`; when `me` is null we render the (V6-restyled) sign-in that
 * links to `/auth/login/start`; when present we mount the AppShell + router.
 *
 * Routing is URL-as-truth via TanStack Router (doc 06). Operational surfaces
 * (Today · Team · Session detail · Verification) run against live backend routes.
 * Setup/Settings screens are permission-gated with `<RequirePermission>` — absent
 * (No-permission screen) when the principal doesn't hold the screen's permission.
 * Anything not yet built resolves to an honest "coming soon" panel.
 */

const rootRoute = createRootRoute({ component: ShellLayout });

function route(path: string, component: () => ReactNode) {
  return createRoute({ getParentRoute: () => rootRoute, path, component });
}

/** Wrap a screen behind its reading permission (doc 13 T0). */
function gated(permission: string, node: ReactNode, what?: string): () => ReactNode {
  return () => (
    <RequirePermission permission={permission} what={what}>
      {node}
    </RequirePermission>
  );
}

/**
 * The two analytics screens, whose ROUTES take either key (F10).
 *
 * `keysThatOpen('dashboard:read')` is the same list `filterNavGroups` gates the
 * rail on and the same list the server's `AGGREGATE_READ` declares, so the
 * route and the rail entry that links to it cannot drift into disagreeing about
 * one reader. See the `ANALYTICS_SURFACE_READ` comment in `features/permissions.ts`.
 *
 * It hands `RequirePermission` the key this reader ACTUALLY HOLDS rather than
 * reimplementing an any-of gate. That keeps the guard's three answers — not yet,
 * could not ask, no — in the one place that has them, which is the whole point
 * of that component: a second copy of "unknown is not denied" is a second copy
 * that can rot. While the set is loading or unreadable `has` is false for every
 * key, the fallback is the screen's named key, and the guard renders its
 * skeleton or its retry exactly as before.
 */
export function GatedAnalytics({ node, what }: { node: ReactNode; what: string }) {
  const { has } = usePermissions();
  const held = keysThatOpen('dashboard:read').find((key) => has(key));
  return (
    <RequirePermission permission={held ?? 'dashboard:read'} what={what}>
      {node}
    </RequirePermission>
  );
}

/**
 * What the analytics screens are, for the denied state.
 *
 * It names the LESSER key as well, because the person most likely to read this
 * is an org admin working out what to grant an outside evaluator, and the
 * honest answer — that there is a de-identified grant which opens this screen
 * without handing over a roster — is not discoverable anywhere else.
 */
export const ANALYTICS_IS =
  'The dashboards are the analytics workspace: headline counts, weekly trends, and program roll-ups for your organization. Two permissions open it. `dashboard:read` shows the identified detail — which worker, which client. `analytics:read` shows the same screens de-identified: organisation-wide totals only, with figures standing on fewer than five people withheld rather than shown as zero.';

/**
 * What the Time clock screens are, for the denied state.
 *
 * These four routes asked for `time:read`, which every worker holds, while
 * every panel on them reads `/api/admin-views/*` — a family guarded by
 * `admin-views:read`, which 1755220000000_admin-views-permission deliberately
 * withholds from worker. The gate now names the key the server checks, so the
 * screens are absent for a principal who could only ever have met a 403 on
 * them; this says what they would have shown.
 */
const ADMIN_VIEWS_IS =
  'The Time clock screens are the administrative view of everybody’s time: who is working right now, how each check-in was verified, and the exceptions somebody needs to look at. Reading your own time does not include reading the organization’s, so this is a separate permission from the one that lets you clock in and see your own sessions.';

/**
 * What location history is, for the denied state.
 *
 * `location_history:view` is granted to no role by default, so the denied state
 * is the FIRST thing many administrators will ever see of this capability. It
 * has to describe the capability, not just name the key.
 */
const LOCATION_HISTORY_IS =
  'Location history shows where a worker clocked in and out, the path their device actually recorded between those points, and the work sites and geofences for the day you choose. It is an audited read: every viewing is recorded against your name.';

/**
 * A Settings screen: the section index beside it, and its own permission gate
 * still applied inside. The layout is chrome — `RequirePermission` remains the
 * thing that decides whether the screen renders, and the server checks again
 * regardless of what the client chose to draw.
 */
function settingsRoute(path: string, permission: string, node: ReactNode) {
  return route(path, () => (
    <SettingsLayout>
      <RequirePermission permission={permission}>{node}</RequirePermission>
    </SettingsLayout>
  ));
}

const indexRoute = route('/', RightNow);
/**
 * VISUAL-PROTOTYPE ONLY: the "Today, reconciled" payoff surface from the
 * liquid-glass exploration. `TodayReconciled.tsx` renders entirely invented
 * figures and names, labelled as concept data on the page itself — but a
 * label on the page is not a guard, and this route used to be registered
 * unconditionally, reachable by any signed-in admin from the real Right Now
 * dashboard with no way to tell the fabricated numbers from real ones short
 * of reading the page copy. `SHOW_CONCEPT_SURFACES` is that guard: the real
 * production entry (`src/main.tsx`) never sets it, so `undefined` is the
 * value in the shipped app and this route and its Right Now link both
 * disappear. Only the fixture harness (`.shots/main.tsx`) sets it, before
 * this module evaluates.
 */
const SHOW_CONCEPT_SURFACES =
  typeof window !== 'undefined' && window.__CWOS_SHOW_CONCEPT_SURFACES__ === true;
const reconciledRoute = SHOW_CONCEPT_SURFACES ? route('/reconciled', TodayReconciled) : null;
const attentionRoute = route('/attention', Attention);
const peopleRoute = route('/people', TeamBoard);
// `/workforce` is the older path for the same screen. Kept so existing links and
// bookmarks resolve, but the rail points at `/field/map` — one screen, one place
// to find it.
const workforceRoute = route(
  '/workforce',
  gated('location_history:view', <Workforce />, LOCATION_HISTORY_IS),
);
const sessionRoute = route('/sessions/$sessionId', SessionDetail);
// Time Clock. The tabs are routes so a link to the exceptions queue is
// something a supervisor can paste into a message.
const timeRoute = route(
  '/time',
  gated(
    ADMIN_VIEWS_READ,
    <TimeClockLayout active="overview">
      <TimeOverview />
    </TimeClockLayout>,
    ADMIN_VIEWS_IS,
  ),
);
const timeWorkingRoute = route(
  '/time/working',
  gated(
    ADMIN_VIEWS_READ,
    <TimeClockLayout active="working">
      <WhosWorking />
    </TimeClockLayout>,
    ADMIN_VIEWS_IS,
  ),
);
const timesheetsRoute = route(
  '/time/timesheets',
  gated(
    'timesheet:read',
    <TimeClockLayout active="timesheets">
      <TimesheetList />
    </TimeClockLayout>,
  ),
);
const timesheetDetailRoute = route(
  '/time/timesheets/$workerId',
  gated('timesheet:read', <TimesheetDetail />),
);
const timeExceptionsRoute = route(
  '/time/exceptions',
  gated(
    ADMIN_VIEWS_READ,
    <TimeClockLayout active="exceptions">
      <TimeExceptions />
    </TimeClockLayout>,
    ADMIN_VIEWS_IS,
  ),
);
// `timesheet:approve` reads through EQUIVALENT_READ_KEYS to DECISION_QUEUE_READ,
// so a reader holding only `timeoff:review` reaches the page and sees the
// time-off section — every other section still refuses on its own terms.
const timeApprovalsRoute = route(
  '/time/approvals',
  gated(
    'timesheet:approve',
    <TimeClockLayout active="approvals">
      <Approvals />
    </TimeClockLayout>,
  ),
);
const workerProfileRoute = route('/people/$workerId', gated('directory:read', <WorkerProfile />));
// `/scheduling`, not `/schedule`: `/schedule` is an API route family the admin
// app proxies, so a browser navigation to it would reach the API and 404. One
// prefix, one owner — the same rule that governs `/compliance`.
const scheduleRoute = route('/scheduling', gated('schedule:read', <Scheduling />));
/**
 * The ask queue. Gated on `schedule:manage` and not on `schedule:read`, because
 * this is a view of the WHOLE roster's outstanding questions — the org-wide key
 * is the one the route itself charges, and gating the screen more loosely than
 * the read behind it would draw a page that then refuses.
 */
const cancellationQueueRoute = route(
  '/scheduling/cancellations',
  gated('schedule:manage', <CancellationQueue />),
);
/**
 * `/visits`, and deliberately NOT `/services` — that prefix is an API family
 * this app proxies, so a browser navigation to it would reach the API and 404.
 * The same rule produced `/schedule` → `/scheduling` and pushed supervision and
 * credentials under `/workforce/*`.
 *
 * `service:read` is the gate because it is the key BOTH reads charge:
 * `GET /services/scheduled` and `GET /services/events` are each
 * `guard(request, reply, 'service:read')`, and the scope of what comes back is
 * settled inside by two other keys the screen reads off the responses. Gating
 * on `schedule:read` — the Scheduling screen's key — would lock a compliance
 * reader out of a surface the server serves them.
 */
const visitsRoute = route('/visits', gated('service:read', <Visits />));
// `/verification` kept as a redirect-free alias: the old rail entry is gone,
// but bookmarks and shared links to it should land somewhere sensible rather
// than 404.
const verificationRoute = route(
  '/verification',
  gated(
    ADMIN_VIEWS_READ,
    <TimeClockLayout active="exceptions">
      <TimeExceptions />
    </TimeClockLayout>,
    ADMIN_VIEWS_IS,
  ),
);
// The canonical home of location history, and what the rail links to. The rail
// item is deliberately NOT permission-filtered (see features/navigation.ts), so
// arriving here without the grant is an expected outcome and must explain both
// what the screen is and what to ask for.
const fieldMapRoute = route(
  '/field/map',
  gated('location_history:view', <Workforce />, LOCATION_HISTORY_IS),
);
const dashboardRoute = route('/dashboard', () => (
  <GatedAnalytics node={<Dashboard />} what={ANALYTICS_IS} />
));
const dashboardsRoute = route('/dashboards', () => (
  <GatedAnalytics node={<ConfigurableDashboard />} what={ANALYTICS_IS} />
));
const messagesRoute = route('/messages', Messages);
const mileageRoute = route('/mileage', gated('mileage:review', <Mileage />));
// Compliance is a top-level area, not a Settings page: an audit log is something
// you consult, not something you configure, and filing evidence next to toggles
// would be the wrong shape.
const complianceRoute = route('/compliance', gated('audit:read', <ComplianceCenter />));
const auditLogRoute = route('/compliance/audit', gated('audit:read', <AuditLog />));
// Supervision and Credentials. Both backends have been complete for some time
// with no screens at all; these are those screens.
//
// The client paths sit under /workforce/* deliberately. `/supervision` and
// `/credentials` are claimed API families in api-families.ts and nginx.conf, so
// a client route on either prefix would be proxied to the API and 404 in a
// browser — the same collision that produced the /schedule → /scheduling and
// /compliance routing fixes.
const supervisionRoute = route(
  '/workforce/supervision',
  gated('supervision:read', <SupervisionCenter />),
);
const supervisionWorkerRoute = route(
  '/workforce/supervision/$workerId',
  gated('supervision:read', <SupervisionWorkerDetail />),
);
const insightsRoute = route('/insights', gated('insights:read', <Insights />, INSIGHTS_IS));
const insightDetailRoute = route('/insights/$findingId', gated('insights:read', <InsightDetail />));
/**
 * THE ESCALATION QUEUE, gated on the key its routes actually charge.
 *
 * `GET /api/insights/escalations/open` asks for `insights:read` AND
 * `directory:read` — an escalation names people, so reading one is reading the
 * workforce. The ROUTE is gated on `insights:read` alone, deliberately: a
 * reader who holds that and not directory access must reach a screen that says
 * the queue is WITHHELD from them, not a 403 door that reads as "no such
 * screen". `EscalationQueue` renders that refusal itself.
 */
const escalationQueueRoute = route(
  '/insights/escalations',
  gated('insights:read', <EscalationQueue />, ESCALATIONS_IS),
);
/**
 * The Intelligence RECORD surfaces (founder ruling 2, 2026-09-08).
 *
 * Gated on `recommendation:read` — the literal
 * `recommendation-record/routes.ts` guards both reads with, and NOT on
 * `outcome:read` or `learning:read`. Those two are checked deeper: the outcome
 * gate asks per record inside the detail read, and the learning section asks
 * for itself. Gating the door on a key the door does not charge would refuse a
 * reader the server would serve.
 */
const recommendationRecordsRoute = route(
  '/intelligence/recommendations',
  gated(RECOMMENDATION_READ_KEY, <RecommendationRecords />, RECOMMENDATION_RECORDS_IS),
);
const recommendationRecordDetailRoute = route(
  '/intelligence/recommendations/$recommendationId',
  gated(RECOMMENDATION_READ_KEY, <RecommendationRecordDetail />),
);
// The Library reads `knowledge:read`, the key every route behind the screen
// checks. The review queue inside it is one key higher (`knowledge:publish`)
// and the screen gates that panel itself, so the door stays at the reading key.
const libraryRoute = route('/library', gated(LIBRARY_READ_KEY, <Library />, LIBRARY_IS));
const librarySourceRoute = route(
  '/library/sources/$sourceId',
  gated(LIBRARY_READ_KEY, <SourceDetail />),
);
// Intelligence -> Automations: the operational face of the rule engine, in
// the Water environment. Configuration stays at /settings/rules.
const automationsRoute = route('/intelligence/automations', gated('rules:read', <Automations />));
const agentsRoute = route('/agents', gated(AGENTS_READ_KEY, <Agents />, AGENTS_IS));
const agentDeployRoute = route(
  '/agents/deploy',
  gated('agents:configure', <AgentDeploy />, AGENT_DEPLOY_IS),
);
const agentDetailRoute = route('/agents/$deploymentId', gated(AGENTS_READ_KEY, <AgentDetail />));
const agentRunRoute = route('/agents/runs/$runId', gated(AGENTS_READ_KEY, <AgentRunDetail />));
const talkToPhoebeRoute = route(
  '/virtual-workforce',
  gated(TALK_TO_PHOEBE_KEY, <TalkToPhoebe />, TALK_TO_PHOEBE_IS),
);
const libraryRuleRoute = route('/library/rules/$ruleId', gated(LIBRARY_READ_KEY, <RuleDetail />));
// Eligibility, forms and incidents (lane DOORS). Paths deliberately share no
// first segment with an API family, the collision that produced the
// /schedule -> /scheduling and /compliance fixes above.
const eligibilityRoute = route(
  '/workforce/eligibility',
  gated('directory:read', <EligibilityCenter />),
);
const eligibilityWorkerRoute = route(
  '/workforce/eligibility/$workerId',
  gated('directory:read', <EligibilityWorkerDetail />),
);
const formsRoute = route('/compliance/forms', gated('knowledge:read', <FormsCenter />));
const formDetailRoute = route('/compliance/forms/$formId', gated('knowledge:read', <FormDetail />));
// ORDER MATTERS. The literal segment must be registered BEFORE the $formId
// route, or `/compliance/forms/responses` is captured as a form id.
//
// The gate is `forms:read_responses`, NOT the `knowledge:read` the two routes
// above take. This is the PHI door: a form response carries what a person
// answered about a client, and `forms/response-reach.ts` decides per row which
// responses answer. Reading the INSTRUMENT and reading the ANSWERS are
// different authorities and the routes say so.
const formResponsesRoute = route(
  '/compliance/forms/responses',
  gated('forms:read_responses', <FormResponseReader />),
);
const formResponseRoute = route(
  '/compliance/forms/responses/$responseId',
  gated('forms:read_responses', <FormResponseReader />),
);
const incidentsRoute = route('/compliance/incidents', gated('incidents:read', <IncidentsCenter />));
const incidentDetailRoute = route(
  '/compliance/incidents/$incidentId',
  gated('incidents:read', <IncidentDetail />),
);
const credentialsRoute = route(
  '/workforce/credentials',
  gated('credential:read', <CredentialsCenter />),
);
const credentialsWorkerRoute = route(
  '/workforce/credentials/$workerId',
  gated('credential:read', <CredentialsWorkerDetail />),
);
// Exports. `/reporting` is not a claimed API family, so the client owns this
// prefix outright — unlike /supervision and /credentials above.
const exportsRoute = route(
  '/reporting/exports',
  gated(
    'reporting:export',
    <ExportsScreen />,
    'Exports lets you download CSVs of the workforce board, exceptions, check-in verification, and approved hours for your organization.',
  ),
);
const documentationRoute = route(
  '/documentation',
  gated('documentation:read', <DocumentationQueue />),
);
/**
 * DOCUMENTATION COMPLETENESS — a THIRD key, and the narrowest of the three.
 *
 * `COMPLETENESS_READ_KEY` rather than a literal, and the constant lives beside
 * the screen, exactly as `TASK_READ_KEY` does — because the key here is NOT the
 * one its two siblings use. `documentation:read` opens
 * `GET /documentation/:eventId`, whose response carries the visit note, so a
 * principal ruling 7 binds to metadata-only cannot hold it. This route's server
 * guard checks `documentation:read_metadata` and nothing else
 * (`documentation/routes.ts:PERMISSION_METADATA`); gating the screen on
 * `documentation:read` would refuse a reader the server would serve, and
 * requiring both would charge a key the server does not check.
 *
 * Registered next to `/documentation/$serviceEventId`. TanStack Router ranks a
 * static segment above a parameter regardless of declaration order — the same
 * property `documentation/routes.ts` relies on and writes down — and
 * `documentation-completeness-personas.test.tsx` mounts `/documentation/
 * completeness` through a real router to prove it rather than trusting it.
 */
const documentationCompletenessRoute = route(
  '/documentation/completeness',
  gated(COMPLETENESS_READ_KEY, <DocumentationCompleteness />, COMPLETENESS_IS),
);
// Intelligence (owner directive §9). `intelligence:use` is what the two routes
// behind the screen check (intelligence/routes.ts guard) — the same key gates
// the rail entry in features/navigation.ts, so the door and the entry cannot
// disagree.
//
// WHO HOLDS IT IS WIDER THAN THE SEED. 1757520000000_intelligence-use-permission
// minted it for org_admin alone; 1757630000000 added `supervisor` and
// 1757640000000 added `worker`, so every shipped global role now holds it and
// this route opens for all of them. The gate stays, because org-local roles can
// still be without it — and because the key is a door rather than a grant: it
// confers no data and no action, and every Intelligence tool re-authorizes
// against the caller's real permissions on every turn. See the fuller note on
// the rail entry in features/navigation.ts, which this comment is the pair to.
const intelligenceRoute = route(
  '/intelligence',
  gated('intelligence:use', <Intelligence />, INTELLIGENCE_IS),
);
const documentationDetailRoute = route(
  '/documentation/$serviceEventId',
  gated('documentation:read', <DocumentationDetail />),
);

/**
 * The canonical Tasks domain — seven routes and a written contract that landed
 * with no screen at all.
 *
 * `TASK_READ_KEY` rather than a literal, and the constant lives beside the
 * screen that uses it. It is `task:read` — the key `tasks/routes.ts` guards
 * every task read on — and the reason it is a constant is that it was
 * `alerts:read` (the key the routes borrowed before 1758090000000) for one
 * commit longer than the server was checking it. A literal here would have been
 * a second copy of that stale binding to find.
 */
const tasksRoute = route('/tasks', gated(TASK_READ_KEY, <TasksQueue />));
const taskDetailRoute = route('/tasks/$taskId', gated(TASK_READ_KEY, <TaskDetail />));

/**
 * `/org-chart`, NOT `/orgchart`.
 *
 * `/orgchart` is an API family (the client calls `/orgchart/users/:id`), and
 * this repository has four production defects from a client route colliding
 * with one — `/compliance`, `/schedule`, `/supervision` and `/credentials` were
 * all renamed for exactly this. The hyphen keeps the first path segment
 * distinct, so no navigation rule has to disambiguate them.
 */
const orgChartRoute = route('/org-chart', gated('orgchart:read', <OrgChart />));
const notificationCenterRoute = route(
  '/notifications',
  gated('notification:read', <NotificationCenter />),
);
const notificationPrefsRoute = route(
  '/settings/notification-preferences',
  gated('notification-preferences:manage-own', <NotificationPreferences />),
);
const channelThreadRoute = route('/messages/$channelId', ChannelThread);
const meProfileRoute = route('/me/profile', MeProfile);
/**
 * WHAT PHIBIAN REMEMBERS ABOUT YOU.
 *
 * TWO DELIBERATE CHOICES IN ONE LINE.
 *
 * `route`, NOT `settingsRoute`, and not `gated`. `settingsRoute` takes a
 * permission and wraps the screen in `RequirePermission`. Every other Settings
 * screen wants that; this one must not have it. The two routes behind it are
 * gated on AUTHENTICATION and the subject column alone, deliberately —
 * `memory:read` governs whether an item may reach a MODEL, is seeded granted to
 * nobody and is marked RESERVED, so gating this screen on it would refuse a
 * person sight of their own record for lacking an operational grant. A data
 * subject's right of access is not a role grant.
 *
 * `/me/`, NOT `/settings/`. Settings is where somebody administers their
 * ORGANIZATION, and every entry in `settingsNav.ts` is filtered by a permission
 * for that reason. This screen is a person's own record; it belongs beside
 * `/me/profile`, which is reachable from the account menu by every principal
 * including one who holds no settings permission at all. Putting it in the
 * Settings centre would also have made `visibleSettings(WORKER)` non-empty —
 * the first permissionless entry in that map — which is a change to what the
 * Settings navigation MEANS, and not one this screen needs.
 */
const memoryAboutMeRoute = route('/me/memory', MemoryAboutMe);
const workerPreviewRoute = route('/preview/worker', WorkerPreview);
// Authenticated-only: everyone can need help.
const helpRoute = route('/help', HelpSupport);

// The Settings centre. `/settings` itself is authenticated-only: it lists the
// sections this principal may read, which for some principals is none — an
// honest empty state, not a 403.
const settingsIndexRoute = route('/settings', () => (
  <SettingsLayout>
    <SettingsIndex />
  </SettingsLayout>
));
// WHERE THE ORGANIZATION OPERATES, gated on its own key rather than org:manage.
// The founder's two-authority ruling makes this tenant CONFIGURATION and keeps
// it apart from platform publication of what external law is; the key exists so
// that whoever may fix the office phone number cannot also decide which state's
// supervision floor reaches a program.
const jurisdictionsRoute = settingsRoute(
  '/settings/jurisdictions',
  'jurisdiction:configure',
  <JurisdictionsSettings />,
);
const onboardingRoute = settingsRoute(
  '/settings/onboarding',
  'onboarding:read',
  <OnboardingChecklist />,
);
const orgRoute = settingsRoute('/settings/org', 'org:manage', <OrgSettings />);
const settingsPeopleRoute = settingsRoute('/settings/people', 'user:read', <PeopleSettings />);
const settingsPersonRoute = settingsRoute(
  '/settings/people/$userId',
  'user:read',
  <PersonDetail />,
);
// Location & geofencing hub: routes gate on the READ permission — the pages
// render read-only for a `location:read` holder, and every write control is
// additionally gated on `location:manage` inside (and again server-side).
const locationRoute = settingsRoute('/settings/location', 'location:read', <LocationGeneralPage />);
// ONE PROGRAM'S LOCATION POLICY — the founder's REVREL follow-up ruling of
// 2026-09-08 ("program overrides are first-class policy configuration and must
// not become a weaker scheduling surface than organization-level policy").
//
// GATED ON `location:read`, exactly like the organization policy pages it
// mirrors, and for the same reason: the page renders read-only for a holder
// without `location:manage` and every write control is disabled inside it (and
// refused again server-side). It additionally READS `GET /programs/:id` for the
// program's name, because every consequence sentence on it names the program —
// the same `program:read` the per-program override list on /settings/location
// already needs to render a single row.
const locationProgramPolicyRoute = settingsRoute(
  '/settings/location/programs/$programId',
  'location:read',
  <ProgramPolicyPage />,
);
const locationTrackingRoute = settingsRoute(
  '/settings/location/tracking',
  'location:read',
  <LocationTrackingPage />,
);
const locationClockRulesRoute = settingsRoute(
  '/settings/location/clock-rules',
  'location:read',
  <LocationClockRulesPage />,
);
// The exceptions QUEUE is worker time data, not policy config, so it gates on
// the API's own read key for that surface (`time:read`), not `location:read`.
const locationExceptionsRoute = settingsRoute(
  '/settings/location/exceptions',
  'time:read',
  <LocationExceptionsPage />,
);
const locationPrivacyRoute = settingsRoute(
  '/settings/location/privacy',
  'location:read',
  <LocationPrivacyPage />,
);
// Activity is the audit centre's record filtered to location, so it gates on
// the audit centre's own key — the server route behind it demands `audit:read`
// and a softer gate here could only ever render a 403.
const locationActivityRoute = settingsRoute(
  '/settings/location/activity',
  'audit:read',
  <LocationActivityPage />,
);
const locationSitesRoute = settingsRoute(
  '/settings/location/sites',
  'location:read',
  <LocationSitesPage />,
);
// Geofences gates on `location:manage`, NOT the hub's `location:read`: the
// page's own list read — `GET /location/geofences` — carries that gate
// server-side (the whole geofence surface is configuration), so a read-gated
// route here could only ever render a 403. The hub sub-nav hides the section
// for the same holder, and the server checks again regardless.
const locationGeofencesRoute = settingsRoute(
  '/settings/location/geofences',
  'location:manage',
  <LocationGeofencesPage />,
);
// Roles & permissions reads with `user:read`, same as the People screens: what
// a role can do is the same class of fact as who holds it, and the server's
// `GET /roles/overview` asks for exactly this key.
const rolesPermissionsRoute = settingsRoute('/settings/roles', 'user:read', <RolesPermissions />);
// Programs and Sites are organization structure: readable by anyone holding the
// domain's read permission, manageable only by a principal the server also lets
// through. The gate here is presentation; every write is checked again server-side.
const programsRoute = settingsRoute('/settings/programs', 'program:read', <ProgramsSettings />);
const programDetailRoute = settingsRoute(
  '/settings/programs/$programId',
  'program:read',
  <ProgramDetail />,
);
const sitesRoute = settingsRoute('/settings/sites', 'location:read', <SitesSettings />);
const siteDetailRoute = settingsRoute('/settings/sites/$siteId', 'location:read', <SiteDetail />);
const rulesRoute = settingsRoute('/settings/rules', 'rules:read', <RulesSettings />);
const servicesRoute = settingsRoute('/settings/services', 'service:read', <ServicesSettings />);
const timeoffRoute = settingsRoute('/settings/timeoff', 'timeoff:read', <TimeoffSettings />);
const notificationsRoute = settingsRoute(
  '/settings/notifications',
  'sms:manage',
  <NotificationsSettings />,
);
const recordsRoute = settingsRoute('/settings/records', 'integrations:read', <RecordsSettings />);
const escalationTargetsRoute = settingsRoute(
  '/settings/escalation-targets',
  'org:manage',
  <EscalationTargetsSettings />,
);
const mileageSettingsRoute = settingsRoute(
  '/settings/mileage',
  'mileage:configure',
  <MileageSettings />,
);
// How rarely Phibian may interrupt one person. Its own key rather than
// `org:manage`, because the founder asked for the smallest door that opens it.
const attentionBudgetRoute = settingsRoute(
  '/settings/attention-budget',
  'insights:attention_budget',
  <AttentionBudgetSettings />,
);
const intelligenceSettingsRoute = settingsRoute(
  '/settings/intelligence',
  'org:manage',
  <IntelligenceSettings />,
);

const routeTree = rootRoute.addChildren([
  indexRoute,
  ...(reconciledRoute ? [reconciledRoute] : []),
  automationsRoute,
  insightsRoute,
  escalationQueueRoute,
  insightDetailRoute,
  recommendationRecordsRoute,
  recommendationRecordDetailRoute,
  libraryRoute,
  librarySourceRoute,
  agentsRoute,
  talkToPhoebeRoute,
  agentDeployRoute,
  agentDetailRoute,
  agentRunRoute,
  libraryRuleRoute,
  eligibilityRoute,
  eligibilityWorkerRoute,
  formsRoute,
  formResponsesRoute,
  formResponseRoute,
  formDetailRoute,
  incidentsRoute,
  incidentDetailRoute,
  attentionRoute,
  peopleRoute,
  workforceRoute,
  sessionRoute,
  verificationRoute,
  timeRoute,
  timeWorkingRoute,
  timesheetsRoute,
  timesheetDetailRoute,
  timeExceptionsRoute,
  timeApprovalsRoute,
  workerProfileRoute,
  scheduleRoute,
  cancellationQueueRoute,
  visitsRoute,
  fieldMapRoute,
  dashboardRoute,
  dashboardsRoute,
  messagesRoute,
  channelThreadRoute,
  mileageRoute,
  complianceRoute,
  auditLogRoute,
  exportsRoute,
  supervisionRoute,
  supervisionWorkerRoute,
  credentialsRoute,
  credentialsWorkerRoute,
  documentationRoute,
  documentationCompletenessRoute,
  documentationDetailRoute,
  tasksRoute,
  taskDetailRoute,
  orgChartRoute,
  intelligenceRoute,
  notificationCenterRoute,
  notificationPrefsRoute,
  meProfileRoute,
  memoryAboutMeRoute,
  workerPreviewRoute,
  helpRoute,
  settingsIndexRoute,
  jurisdictionsRoute,
  onboardingRoute,
  orgRoute,
  settingsPeopleRoute,
  settingsPersonRoute,
  rolesPermissionsRoute,
  locationRoute,
  locationProgramPolicyRoute,
  locationTrackingRoute,
  locationClockRulesRoute,
  locationExceptionsRoute,
  locationPrivacyRoute,
  locationActivityRoute,
  locationSitesRoute,
  locationGeofencesRoute,
  programsRoute,
  programDetailRoute,
  sitesRoute,
  siteDetailRoute,
  rulesRoute,
  servicesRoute,
  timeoffRoute,
  notificationsRoute,
  recordsRoute,
  mileageSettingsRoute,
  escalationTargetsRoute,
  attentionBudgetRoute,
  intelligenceSettingsRoute,
]);

/**
 * VISUAL-PROTOTYPE ONLY: the fixture harness (`.shots/`) sets this global so a
 * static export of the prototype can be hosted at any sub-path — hash history
 * keeps every navigation inside the page URL instead of rewriting the path,
 * which would break relative asset resolution on a static host. The real app
 * never sets it and keeps browser history exactly as before.
 */
declare global {
  interface Window {
    __CWOS_HASH_HISTORY__?: RouterHistory;
    /**
     * VISUAL-PROTOTYPE ONLY: gates concept-data routes (currently
     * `/reconciled`) that must never be reachable in the real product. Set
     * only by the fixture harness (`.shots/main.tsx`), before this module
     * evaluates. See `reconciledRoute` above.
     */
    __CWOS_SHOW_CONCEPT_SURFACES__?: boolean;
  }
}

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: ComingSoon,
  ...(typeof window !== 'undefined' && window.__CWOS_HASH_HISTORY__
    ? { history: window.__CWOS_HASH_HISTORY__ }
    : {}),
});

function ComingSoon() {
  return (
    <div className="rn-card">
      <h2 className="rn-card-title">This area is on the way</h2>
      <p className="rn-muted">
        The number that brought you here is real — this screen just hasn’t shipped in this build
        yet.
      </p>
    </div>
  );
}

/**
 * "We couldn't check your session" — said plainly, with the two things a person
 * can actually do about it. It never claims the session ended, because we do
 * not know that.
 */
function SessionUnavailable({ onRetry, onSignIn }: { onRetry: () => void; onSignIn: () => void }) {
  return (
    <main className="splash">
      <div className="rn-card" role="alert" style={{ maxWidth: 460 }}>
        <h2 className="rn-card-title">We couldn’t check your session just now</h2>
        <p className="rn-muted">
          This is a problem reaching Phibian, not a problem with your account — you have not been
          signed out. Nothing you were working on has been lost.
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-12)',
            marginTop: 'var(--space-12)',
          }}
        >
          <button type="button" className="rn-btn-quiet" onClick={onRetry}>
            Try again
          </button>
          <button type="button" className="rn-link" onClick={onSignIn}>
            Sign in instead
          </button>
        </div>
      </div>
    </main>
  );
}

// Module scope, before first paint: the theme must be on <html> before any
// surface renders, or the first frame flashes the previous UI.
installTheme();

export function App() {
  return (
    <AuthProvider>
      <Root />
    </AuthProvider>
  );
}

/**
 * The organization chooser renders pre-session, so it cannot live in the router
 * (which only mounts once `me` exists). The OIDC callback performs a full-page
 * redirect here, so reading the location directly is both sufficient and
 * correct.
 */

function Root() {
  const { me, loading, status, retry, login } = useAuth();
  // Set only by the escape hatch on the unavailable screen: someone who really
  // does want to sign in should never be trapped behind a retry button.
  const [signInAnyway, setSignInAnyway] = useState(false);

  if (loading) {
    return <main className="splash">Checking session…</main>;
  }

  // An invitation landing renders whether or not a session exists, and is
  // checked FIRST. Someone arriving from a "Join Phibian" email has no account
  // yet, so falling through to sign-in would ask them to authenticate as
  // somebody they are not — a dead end that looks like a working page. It also
  // takes precedence over an EXISTING session: a signed-in administrator
  // opening an invitation meant for a colleague should see that invitation, not
  // be silently dropped into their own dashboard.
  //
  // The token arrives in the FRAGMENT (`/invite#<token>`), which is never sent
  // to a server and so cannot reach an access log; the legacy path form is
  // still read for links already sitting in inboxes. See lib/invitePath.ts.
  const invitePathname = window.location.pathname;
  if (isInvitePath(invitePathname)) {
    const token = inviteTokenFromLocation(invitePathname, window.location.hash);
    if (token) return <AcceptInvite token={token} />;
  }

  if (!me) {
    // Multi-membership login: a signed org-selection cookie exists but no
    // session does. Without this branch the redirect lands on the sign-in
    // screen and the user can never get in (see SelectOrg for the full note).
    if (isSelectOrgPath(window.location.pathname)) {
      return <SelectOrg />;
    }
    // We could not reach `/auth/me`, which is not the same as being signed out.
    // Showing the sign-in screen here would tell an authenticated
    // administrator, falsely, that their session had ended — and asking them to
    // sign in again cannot work while the thing that failed is still failing.
    if (status === 'unavailable' && !signInAnyway) {
      return <SessionUnavailable onRetry={retry} onSignIn={() => setSignInAnyway(true)} />;
    }
    return <SignIn onDevLogin={login} />;
  }

  // A privileged account with a valid session but no second factor gets
  // enrollment INSTEAD of the app, not alongside it. Every route that reads
  // tenant data is refused server-side until the factor exists, so rendering
  // the router here would produce an application where nothing works and
  // nothing on screen says why. There is deliberately no way to DISMISS it —
  // but EnrollMfa now always renders a sign-out, because "no way past" and "no
  // way out" are different things and this line used to license both.
  if (me.mfaEnrollmentRequired) {
    /**
     * THE AUTHENTICATION IDENTITY, NOT `me.email`.
     *
     * This line used to pass `me.email` — `app_user.email`, the per-organization
     * application profile — and the screen rendered it as "Signed in as" and
     * "Confirm your password for". The founder reached it after signing in with
     * his real Google account and was told he was somebody else, and then asked
     * for a password belonging to an identity that holds none.
     *
     * `undefined` when the server did not say. EnrollMfa then names no address
     * at all, which is the honest rendering: the profile address is not a
     * fallback for this question, it is the wrong answer to it.
     */
    return <EnrollMfa authnEmail={signedInAs(me)} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
